import React from "react";
import { App as AntdApp, Button, Dropdown } from "antd";
import { DownloadOutlined, MoreOutlined, FileImageOutlined, DeleteOutlined, RollbackOutlined, CloudUploadOutlined, EditOutlined, FileExcelOutlined, FormOutlined, ExclamationCircleOutlined, CopyOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import api from "@/api/axios.js";
import UploadRefsModal from "./UploadRefsModal.jsx";
import UploadExcelCodesModal from "./UploadExcelCodesModal.jsx";
import UploadPdfModal from "./UploadPdfModal.jsx";
import EditFlyerModal from "./EditFlyerModal.jsx";
import FlyerErrorModal from "./FlyerErrorModal.jsx";
import { triggerBrowserDownload } from "../utils/download.js";

export default function FlyerActionsMenu({ row, onManage, onRefresh, variant = "default" }) {
	const navigate = useNavigate();
	const { message, modal } = AntdApp.useApp();
	const [refsModalOpen, setRefsModalOpen] = React.useState(false);
	const [excelModalOpen, setExcelModalOpen] = React.useState(false);
	const [pdfModalOpen, setPdfModalOpen] = React.useState(false);
	const [editModalOpen, setEditModalOpen] = React.useState(false);
	const [errorModalOpen, setErrorModalOpen] = React.useState(false);
	const [actionLoading, setActionLoading] = React.useState(false);

	const stopRowPropagation = (event) => event?.stopPropagation?.();

	const goManage = () => {
		if (typeof onManage === 'function') return onManage(row);
		navigate(`/flyermanage/${row._id || row.id}`);
	};

	const downloadBlob = async ({ url, loadingMsg, successMsg }) => {
		setActionLoading(true);
		const hideLoading = message.loading(loadingMsg, 0);
		try {
			const flyerId = row._id || row.id;
			triggerBrowserDownload(url.replace('{id}', flyerId));
			if (successMsg) message.success(successMsg);
		}
		catch (e) {
			message.error(`Errore export: ${e.message}`);
		}
		finally {
			hideLoading();
			setActionLoading(false);
		}
	};

	const onExportResources = () => downloadBlob({
		url: '/api/flyers/{id}/export',
		loadingMsg: 'Esportazione in corso...',
		successMsg: 'Esportazione completata!',
	});

	const onExportPdf = () => downloadBlob({
		url: '/api/flyers/{id}/export.pdf',
		loadingMsg: 'Download PDF in corso...',
	});

	const onExportXml = () => downloadBlob({
		url: '/api/flyers/{id}/export.xml',
		loadingMsg: 'Download XML in corso...',
	});

	const onExportPages = () => downloadBlob({
		url: '/api/flyers/{id}/export.pages',
		loadingMsg: 'Download Pagine in corso...',
	});

	const onExportRefs = () => downloadBlob({
		url: '/api/flyers/{id}/export.refs',
		loadingMsg: 'Download Referenze in corso...',
	});

	const onDelete = () => {
		const flyerId = row._id || row.id;
		modal.confirm({
			title: "Spostare nel cestino?",
			content: "Il volantino non sarà più visibile in elenco e finirà nel cestino. Potrai ripristinarlo.",
			okText: "Sposta nel cestino",
			cancelText: "Annulla",
			onOk: async () => {
				setActionLoading(true);
				try {
					await api.patch(`/api/flyers/${flyerId}/delete`);
					message.success("Volantino spostato nel cestino");
					onRefresh?.();
				}
				catch (error) {
					message.error(error?.response?.data?.error || "Eliminazione non riuscita");
				}
				finally {
					setActionLoading(false);
				}
			}
		});
	};

	const onDuplicate = () => {
		const flyerId = row._id || row.id;
		modal.confirm({
			title: "Duplicare il volantino?",
			content: `Verrà creato "${row.name || "Volantino"} (copia)" nella stessa promo, con pagine, gabbie, card e referenze identiche. Le modifiche alla copia non toccano l'originale.`,
			okText: "Duplica",
			cancelText: "Annulla",
			onOk: async () => {
				setActionLoading(true);
				const hideLoading = message.loading("Duplicazione in corso...", 0);
				try {
					const { data } = await api.post(`/api/flyers/${flyerId}/duplicate`);
					message.success(`Creato "${data?.flyer?.name || "copia"}"`);
					onRefresh?.();
				}
				catch (error) {
					message.error(error?.response?.data?.error || "Duplicazione non riuscita");
				}
				finally {
					hideLoading();
					setActionLoading(false);
				}
			}
		});
	};

	const onRestore = () => {
		const flyerId = row._id || row.id;
		modal.confirm({
			title: "Ripristinare il volantino?",
			okText: "Ripristina",
			cancelText: "Annulla",
			onOk: async () => {
				setActionLoading(true);
				try {
					await api.patch(`/api/flyers/${flyerId}/restore`);
					message.success("Volantino ripristinato");
					onRefresh?.();
				}
				catch (error) {
					message.error(error?.response?.data?.error || "Ripristino non riuscito");
				}
				finally {
					setActionLoading(false);
				}
			}
		});
	};

	const onHardDelete = () => {
		const flyerId = row._id || row.id;
		modal.confirm({
			title: "Elimina definitivamente?",
			content: "Questa azione rimuove dati e file locali. Non sarà possibile recuperarli.",
			okText: "Elimina",
			okButtonProps: { danger: true },
			cancelText: "Annulla",
			onOk: async () => {
				setActionLoading(true);
				try {
					await api.delete(`/api/flyers/${flyerId}`);
					message.success("Volantino eliminato definitivamente");
					onRefresh?.();
				}
				catch (error) {
					message.error(error?.response?.data?.error || "Eliminazione definitiva non riuscita");
				}
				finally {
					setActionLoading(false);
				}
			}
		});
	};

	const onUploadRefs = () => setRefsModalOpen(true);

	const onCloseRefsModal = (event) => {
		stopRowPropagation(event);
		setRefsModalOpen(false);
	};

	const items = React.useMemo(() => {
		if (variant === "trash") {
			return [
				{ key: "restore", label: "Ripristina", icon: <RollbackOutlined /> },
				{
					key: "hard-delete",
					label: <span style={{ color: '#c00' }}>Elimina definitivamente</span>,
					icon: <DeleteOutlined style={{ color: "#c00" }} />
				}
			];
		}

		const isProcessing = row.status === "PROCESSING";
		const progressText = isProcessing && row.processingProgress > 0 ? ` (${row.processingProgress}%)` : "";

		return [
			{ key: 'manage', label: 'Gestisci' },
			{ key: 'edit-info', label: 'Modifica informazioni', icon: <FormOutlined /> },
			{ key: 'duplicate', label: 'Duplica volantino', icon: <CopyOutlined />, disabled: isProcessing },
			{ key: "error-report", label: "Gestisci errori", icon: <ExclamationCircleOutlined style={{ color: "#d4380d" }} /> },
			{
				key: 'open-preprocess',
				label: `Apri Pre-elaborazione${progressText}`,
				icon: <EditOutlined />,
				disabled: !(row.pageWidth > 0 || isProcessing)
			},
			{ key: 'preprocess', label: row.pageWidth > 0 ? 'Ricarica/Cambia PDF' : 'Pre-elabora PDF', icon: <CloudUploadOutlined /> },
			{ key: 'excel', label: "Carica Codici Clienti", icon: <FileExcelOutlined /> },
			{ key: 'refs', label: 'Carica Referenze Prodotto', icon: <FileImageOutlined /> },
			{
			key: 'export',
			label: 'Esporta Risorse',
			icon: <DownloadOutlined />,
			children: [
				{ key: 'export-pdf', label: 'Volantino (PDF)' },
				{ key: 'export-xml', label: 'Solo XML' },
				{ key: 'export-pages', label: 'Solo Pagine (ZIP)' },
				{ key: 'export-refs', label: 'Solo Referenze (ZIP)' },
				{ type: 'divider' },
				{ key: 'export-all', label: 'Esporta tutto (ZIP)' },
			],
		},
			{ type: 'divider' },
			{ key: 'del', label: <span style={{ color: '#c00' }}>Elimina</span>, icon: <DeleteOutlined style={{ color: "#c00" }} /> },
		];
	}, [variant, row.pageWidth, row.status, row.processingProgress]);

	const handleMenuClick = (info) => {
		info.domEvent?.stopPropagation?.();

		switch (info.key) {
			case 'manage':
				goManage();
				break;
			case 'edit-info':
				setEditModalOpen(true);
				break;
			case 'duplicate':
				onDuplicate();
				break;
			case "error-report":
				setErrorModalOpen(true);
				break;
			case 'open-preprocess':
				if (row.isVariant && row.parentFlyerId) {
					navigate(`/variant-preprocess/${row._id || row.id}`);
				} else {
					navigate(`/flyerpreprocess/${row._id || row.id}`);
				}
				break;
			case 'excel':
				setExcelModalOpen(true);
				break;
			case 'preprocess':
				setPdfModalOpen(true);
				break;
			case 'refs':
				onUploadRefs();
				break;
			case 'export-pdf':
				onExportPdf();
				break;
			case 'export-xml':
				onExportXml();
				break;
			case 'export-pages':
				onExportPages();
				break;
			case 'export-refs':
				onExportRefs();
				break;
			case 'export-all':
				onExportResources();
				break;
			case 'del':
				onDelete();
				break;
			case "restore":
				onRestore();
				break;
			case "hard-delete":
				onHardDelete();
				break;
		}
	};

	return (
		<div
			onClick={stopRowPropagation}
			onMouseDown={stopRowPropagation}
			style={{ display: "inline-block" }}
		>
			<Dropdown
				trigger={['click']}
				placement="bottomRight"
				menu={{ items, onClick: handleMenuClick }}
			>
				<Button icon={<MoreOutlined />} loading={actionLoading} />
			</Dropdown>

			{variant === "default" && (
				<>
					<UploadRefsModal
						flyerId={row._id || row.id}
						open={refsModalOpen}
						onClose={onCloseRefsModal}
						onComplete={() => {
							message.success("Referenze caricate con successo!");
							try {
								onRefresh?.();
							}
							catch {
							}
						}}
					/>
					<UploadExcelCodesModal
						flyerId={row._id || row.id}
						open={excelModalOpen}
						onClose={() => setExcelModalOpen(false)}
						onOpenCard={(item) => {
							setExcelModalOpen(false);
							const page = item.page != null ? `/${item.page}` : "";
							navigate(`/flyermanage/${row._id || row.id}${page}`, { state: { flyerName: row.name, focusCardId: String(item.cardId) } });
						}}
						onComplete={() => {
							// La modale resta aperta: mostra il resoconto dell'import.
							onRefresh?.();
						}}
					/>
					<UploadPdfModal
						flyerId={row._id || row.id}
						open={pdfModalOpen}
						onClose={() => setPdfModalOpen(false)}
						preserveZones={Number(row.cardsCount || row.zonesCount || 0) > 0}
						onComplete={() => {
							setPdfModalOpen(false);
							const flyerId = row._id || row.id;
							navigate(`/flyerpreprocess/${flyerId}`);
						}}
					/>
					<EditFlyerModal
						open={editModalOpen}
						onClose={() => setEditModalOpen(false)}
						flyer={row}
						onUpdated={() => onRefresh?.()}
					/>
					<FlyerErrorModal
						open={errorModalOpen}
						onClose={() => setErrorModalOpen(false)}
						flyerId={row._id || row.id}
						flyerName={row.name}
						onUpdated={() => onRefresh?.()}
					/>
				</>
			)}
		</div>
	);
}
